import DistributionList from "../../models/DistributionsList";
import MsLanguageValueObjList from "../../models/MsLanguageValueObjList";
import AdmsIdentifierList from "../../models/AdmsIdentifierList";
import GenericLangValue from "../../models/GenericLangValue";
import GenericTextValue from "../../models/GenericTextValue";
import GenericLangValueList from "../../models/GenericLangValueList";
import GenericIdValueList from "../../models/GenericIdValueList";
import GenericIdValue from "../../models/GenericIdValue";
import IsDocumentedByList from "../../models/IsDocumentedByList";
import HasOriginalSourceObjList from "../../models/HasOriginalSourceObjList";
import MsLanguageResourceList from "../../models/MsLanguageResourceList";
import GenericBooleanValue from "../../models/GenericBooleanValue";
import GenericStringValue from "../../models/GenericStringValue";
import GenericBooleanRadioValue from "../../models/GenericBooleanRadioValue";
import ModelDistributionList from "../../models/ModelDistributionList";
import EnvironmentalImpactObjList from "../../models/EnvironmentalImpactObjList";
import MsLanguageResourceSingleItem from "../../models/MsLanguageResourceSingleItem";
import ModalityList from "../../models/ModalityList";
import EvaluationObjList from "../../models/EvaluationObjList";
import TypeValue from "../../models/TypeValue";
import PublisherList from "../../models/PublisherList";
import SoftwareDistributionList from "../../models/SoftwareDistributionList";
import ProcessingResourceList from "../../models/ProcessingResourceList";
import HasBenchmarkList from "../../models/HasBenchmarkList";
import TemporalList from "../../models/TemporalList";
import {keys} from "../../config/nameSpacesKeys";
import {isEmpty} from "../../config/commonFunctions";

export function useLandingPageData({expanded, mappings, defaultLanguage, langDcatVersion}) {
  const properties = expanded;

  // ── helpers ───────────────────────────────────────────────────────────────

  function getLabel(key, defaultValue) {
    const returnValue = mappings[key] || defaultValue;
    return returnValue || key;
  }

  function normalizeBreaks(content) {
    if (!content) return "";
    return content.replace(/<br\s*\/?>/gi, "\n");
  }

  // ── lrType ────────────────────────────────────────────────────────────────

  const lrTypeObj = new GenericIdValue("lrType", properties, keys.lrType);
  const lrType = lrTypeObj.getValue();

  const isML = lrType.includes("mlModel") || lrType.includes("mLModel") || lrType.includes("machineLearningModel");
  const isCorpus = lrType.includes("corpus");
  const isLcr = lrType.includes("lexicalConceptualResource");
  const isToolService = lrType.includes("toolService");

  // ── common fields ─────────────────────────────────────────────────────────
  const publicItem = new GenericTextValue("public", properties, keys.publicItem);
  const externalItem = new GenericTextValue("public", properties, keys.externalItem);
  const title = new GenericLangValue("title", properties, keys.title);
  const alternative = new GenericLangValueList("alternative", properties, keys.alternative);
  const description = new GenericLangValue("description", properties, keys.description);
  const anDetails = new GenericLangValue("anonymizationDetails", properties, keys.anonymizationDetails);
  const perDetails = new GenericLangValue("personalDataDetails", properties, keys.personalDataDetails);
  const senDetails = new GenericLangValue("sensitiveDataDetails", properties, keys.sensitiveDataDetails);
  const version = new GenericTextValue("version", properties, keys.version);
  const keywords = new GenericLangValueList("keyword", properties, keys.keyword);
  const domain = new GenericIdValueList("domain", properties, keys.domain);
  const anonymized = new GenericIdValue("anonymized", properties, keys.anonymized);
  const sensitiveDataIncluded = new GenericIdValue("sensitiveDataIncluded", properties, keys.sensitiveDataIncluded);
  const personalDataIncluded = new GenericIdValue("personalDataIncluded", properties, keys.personalDataIncluded);
  const lingualityType = new GenericIdValue("lingualityType", properties, keys.lingualityType);
  const lcrSubclass = new GenericIdValue("lcrSubclass", properties, keys.lcrSubclass);
  const multilingualityType = new GenericIdValue("multilingualityType", properties, keys.multilingualityType);
  const annotationType = new GenericIdValueList("annotationType", properties, keys.annotationType);
  const corpusSubclass = new GenericIdValue("corpusSubclass", properties, keys.corpusSubclass);
  const mediaType = new GenericIdValueList("mediaType", properties, keys.msmediaType);
  const linguisticInformation = new GenericIdValueList("linguisticInformation", properties, keys.linguisticInformation);
  const conformsTo = new GenericIdValueList("conformsTo", properties, keys.conformsTo);
  const spatial = new GenericIdValueList("spatial", properties, keys.spatial);
  const modelFunction = new GenericIdValueList("modelFunction", properties, keys.modelFunction);

  // ── language lists ────────────────────────────────────────────────────────

  const msLanguageList = new MsLanguageValueObjList("language", properties, keys.mslanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);
  const pivotLanguageList = new MsLanguageValueObjList("pivotLanguage", properties, keys.pivotLanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);
  const sourceLanguageList = new MsLanguageValueObjList("sourceLanguage", properties, keys.sourceLanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);
  const targetLanguageList = new MsLanguageValueObjList("targetLanguage", properties, keys.targetLanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);
  const metalanguageList = new MsLanguageValueObjList("metalanguage", properties, keys.metalanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);
  // v0_9_5 / ML: input and output language replace msLanguageList
  const inputLanguage = new MsLanguageValueObjList("inputLanguage", properties, keys.inputLanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);
  const outputLanguage = new MsLanguageValueObjList("outputLanguage", properties, keys.outputLanguage, keys.languageCode, keys.languageVarietyName, keys.region, keys.script, keys.variant);

  // ── provenance / documentation ────────────────────────────────────────────

  const hasOriginalSource = new HasOriginalSourceObjList("hasOriginalSource", properties, keys.hasOriginalSource, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const originalSourceDescription = new GenericLangValue("originalSourceDescription", properties, keys.originalSourceDescription);
  const publisher = new PublisherList("publisher", properties, keys.publisher, keys.admsIdentifier, keys.notation, keys.schemaAgency, keys.agentType);
  const iprHolder = new PublisherList("iprHolder", properties, keys.iprHolder, keys.admsIdentifier, keys.notation, keys.schemaAgency, keys.agentType);
  const isDocumentedBy = new IsDocumentedByList("isDocumentedBy", properties, keys.isDocumentedBy, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const admsIdentifierList = properties[keys.admsIdentifier] ? new AdmsIdentifierList("admsIdentifier", properties, keys.admsIdentifier, keys.notation, keys.schemaAgency) : null;
  const temporal = properties[keys.temporal] ? new TemporalList("temporal", properties, keys.temporal, keys.startDate, keys.endDate) : null;

  // corpus 0.9.5 extras
  const generationModel = (isCorpus && langDcatVersion === "0.9.5") ? new MsLanguageResourceSingleItem("generationModel", properties, keys.generationModel, keys.admsIdentifier, keys.notation, keys.schemaAgency): null;
  const seedDataset = new MsLanguageResourceList("seedDataset", properties, keys.seedDataset, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const generationProcess = new GenericLangValue("generationProcess", properties, keys.generationProcess);
  const generationType = new GenericIdValue("generationType", properties, keys.generationType);
  const languageType = new GenericIdValue("languageType", properties, keys.languageType);
  const creator = new PublisherList("dctCreator", properties, keys.dctCreator, keys.admsIdentifier, keys.notation, keys.schemaAgency, keys.agentType);

  // ── privacy ───────────────────────────────────────────────────────────────

  const hasTechnicalOrganisationalMeasure = new GenericIdValueList("hasTechnicalOrganisationalMeasure", properties, keys.hasTechnicalOrganisationalMeasure);
  const dataProtectionPrincipleApplied = new GenericIdValueList("dataProtectionPrincipleApplied", properties, keys.dataProtectionPrincipleApplied);

  // ── distributions ─────────────────────────────────────────────────────────

  // Standard distribution: corpus + lcr + mlModel or !toolService (in default)
  const distributionsList = (isCorpus || isLcr || (isML && langDcatVersion === "0.9.3")) ? new DistributionList("distribution", properties, keys.distribution, keys.byteSize, keys.packageFormat, keys.format, keys.size) : [];
  const softwareDistributionList = isToolService ? new SoftwareDistributionList("softwareDistribution", properties, keys.softwareDistributionProperty, keys.downloadLocation, keys.accessLocation, keys.executionLocation, keys.softwareDistributionForm) : [];
  // v0_9_5 / ML
  const modelDistributionsList = (isML && langDcatVersion === "0.9.5") ? new ModelDistributionList("modelDistribution", properties, keys.modelDistribution) : [];

  // ── tool service ──────────────────────────────────────────────────────────
  const msfunction = new GenericIdValueList("msfunction", properties, keys.msfunction);
  const languageDependent = new GenericBooleanRadioValue("languageDependent", properties, keys.languageDependent);
  const inputContentResourceList = isToolService ? new ProcessingResourceList("inputContentResource", properties, keys.inputContentResource, keys.msmediaType, keys.format, keys.processingResourceType) : [];
  const outputResourceList = isToolService ? new ProcessingResourceList("outputResource", properties, keys.outputResource, keys.msmediaType, keys.format, keys.processingResourceType) : [];

  // ── ML model core ─────────────────────────────────────────────────────────
  const modelType = new GenericIdValue("modelType", properties, keys.modelType);
  const trainedOn = new MsLanguageResourceList("trainedOn", properties, keys.trainedOn, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const evaluationDataset = new MsLanguageResourceList("evaluationDataset", properties, keys.evaluationDataset, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const finetuneDataset = new MsLanguageResourceList("finetuneDataset", properties, keys.finetuneDataset, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const variantOf = new MsLanguageResourceList("variantOf", properties, keys.variantOf, keys.admsIdentifier, keys.notation, keys.schemaAgency);
  const bias = new GenericLangValue("bias", properties, keys.bias);
  const limitations = new GenericLangValue("limitations", properties, keys.limitations);
  const creationDetails = new GenericLangValue("creationDetails", properties, keys.creationDetails);
  const evaluationResults = new GenericLangValue("evaluationResults", properties, keys.evaluationResults);
  const quantizationProcess = new GenericLangValue("quantizationProcess", properties, keys.quantizationProcess);
  const parameterPrecision = new GenericStringValue("parameterPrecision", properties, keys.parameterPrecision);
  const contextLength = new GenericTextValue("contextLength", properties, keys.contextLength);
  const quantized = new GenericBooleanValue("quantized", properties, keys.quantized);

  // ── ML model v0_9_5 extras ────────────────────────────────────────────────
  const finetuningDescription = new GenericLangValue("finetuningDescription", properties, keys.finetuningDescription);
  const finetuningHardware = new GenericTextValue("finetuningHardware", properties, keys.finetuningHardware);
  const baseModel = new MsLanguageResourceList("baseModel", properties, keys.baseModel, keys.admsIdentifier, keys.notation, keys.schemaAgency);

  const intendedUse = new GenericLangValue("intendedUse", properties, keys.intendedUse);
  const instructions = new GenericLangValue("instructions", properties, keys.instructions);
  const designSpecifications = new GenericLangValue("designSpecifications", properties, keys.designSpecifications);
  const requiresSoftware = new GenericLangValue("requiresSoftware", properties, keys.requiresSoftware);
  const finetuningMethodology = new GenericLangValue("finetuningMethodology", properties, keys.finetuningMethodology);
  const hasInputModalityList = isML ? new ModalityList("hasInputModality", properties, keys.hasInputModality) : [];
  const hasOutputModalityList = isML ? new ModalityList("hasOutputModality", properties, keys.hasOutputModality) : [];
  const hasInferencingImpact = isML ? new EnvironmentalImpactObjList("hasInferencingImpact", properties, keys.hasInferencingImpact) : [];
  const hasTrainingImpact = isML ? new EnvironmentalImpactObjList("hasTrainingImpact", properties, keys.hasTrainingImpact) : [];
  const hasBenchmark = isML ? new HasBenchmarkList("hasBenchmark", properties, keys.hasBenchmark) : [];
  const releaseDate = new TypeValue("releaseDate", properties, keys.releaseDate, keys.dateType);
  const modified = new TypeValue("modified", properties, keys.modified, keys.dateType);
  const evaluation = new EvaluationObjList("evaluation", properties, keys.evaluation);
  // ── visibility flags ──────────────────────────────────────────────────────

  const hasCategoriesDisplay =
    !domain.isEmpty() ||
    !isEmpty(spatial.getList()) ||
    (temporal && temporal.getObjList().length > 0) ||
    !isEmpty(getLabel(multilingualityType.getValue())) ||
    !isEmpty(getLabel(corpusSubclass.getValue())) ||
    !isEmpty(annotationType.getList()) ||
    !linguisticInformation.isEmpty() ||
    !isEmpty(getLabel(lcrSubclass.getValue()));

  const hasLanguageLists =
    pivotLanguageList.getLanguagesList().length > 0 ||
    sourceLanguageList.getLanguagesList().length > 0 ||
    targetLanguageList.getLanguagesList().length > 0 ||
    metalanguageList.getLanguagesList().length > 0 ||
    outputLanguage.getLanguagesList().length > 0;

  const hasProvenanceExtras =
    (seedDataset && !seedDataset.isEmpty()) ||
    (generationModel && !generationModel.isEmpty()) ||
    (generationProcess && !generationProcess.isEmpty());

  const hasProvenanceMain =
    !iprHolder.isEmpty() ||
    !creator.isEmpty() ||
    !hasOriginalSource.isEmpty() ||
    !originalSourceDescription.isEmpty();

  const hasDocumentation = !isDocumentedBy.isEmpty();

  const hasProvenance = hasProvenanceMain || hasProvenanceExtras;

  // hasModelProperties covers both default and v0_9_5 fields so both
  // versions get the correct truthy value regardless of which fields exist
  const hasModelProperties =
    !trainedOn.isEmpty() || !evaluationDataset.isEmpty() || !finetuneDataset.isEmpty() ||
    !variantOf.isEmpty() || !bias.isEmpty() || !limitations.isEmpty() ||
    !creationDetails.isEmpty() || !evaluationResults.isEmpty() ||
    !isEmpty(contextLength.getValue()) || !quantizationProcess.isEmpty() ||
    quantized.isEmpty() || !parameterPrecision.isEmpty() ||
    !isEmpty(getLabel(modelType.getValue())) ||
    // v0_9_5 extras (empty() returns true when missing, so safe for default)
    !instructions.isEmpty() || !designSpecifications.isEmpty() ||
    !requiresSoftware.isEmpty() || !finetuningMethodology.isEmpty() ||
    (hasBenchmark?.getObjList?.().length > 0) ||
    (hasInferencingImpact?.getObjList?.().length > 0) ||
    (hasTrainingImpact?.getObjList?.().length > 0) || !baseModel.isEmpty() || !finetuningDescription.isEmpty();

  const columnCount = [
    hasCategoriesDisplay,
    hasLanguageLists,
    hasProvenance || hasDocumentation || hasModelProperties,
  ].filter(Boolean).length;

  const regex = /(<([^>]+)>)/gi;

  return {
    // helpers
    getLabel, normalizeBreaks, regex, publicItem, externalItem,

    // lrType
    lrType, isML, isCorpus, isLcr, isToolService,

    // common
    title, alternative, description, version, keywords,
    domain, conformsTo, spatial, temporal, mediaType,
    lingualityType, lcrSubclass, multilingualityType, annotationType,
    corpusSubclass, linguisticInformation, modelFunction,
    anonymized, sensitiveDataIncluded, personalDataIncluded,
    anDetails, perDetails, senDetails,
    hasTechnicalOrganisationalMeasure, dataProtectionPrincipleApplied,

    // people / docs
    publisher, iprHolder, isDocumentedBy, admsIdentifierList,
    hasOriginalSource, originalSourceDescription,

    // language lists
    msLanguageList, pivotLanguageList, sourceLanguageList,
    targetLanguageList, metalanguageList,
    inputLanguage, outputLanguage,

    // distributions
    distributionsList, softwareDistributionList, modelDistributionsList,

    // tool service
    msfunction, languageDependent, inputContentResourceList, outputResourceList,

    // corpus 0.9.5
    languageType, generationProcess, seedDataset, generationModel, generationType, creator,

    // ML core
    modelType, trainedOn, evaluationDataset, finetuneDataset, variantOf,
    bias, limitations, creationDetails, evaluationResults,
    quantizationProcess, parameterPrecision, contextLength, quantized,

    // ML v0_9_5 extras
    intendedUse, instructions, designSpecifications, requiresSoftware,
    finetuningMethodology, hasInputModalityList, hasOutputModalityList,
    hasInferencingImpact, hasTrainingImpact, hasBenchmark,
    releaseDate, modified, evaluation, baseModel, finetuningHardware, finetuningDescription,

    // visibility flags
    hasCategoriesDisplay, hasLanguageLists, hasProvenance,
    hasDocumentation, hasModelProperties, columnCount,
  };
}
